import { useState, useEffect } from 'react';
import { Lock, Mail, Eye, EyeOff, AlertCircle, X } from 'lucide-react';
import SellGoLogo from '@/components/SellGoLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { getOdooDatabase, setApiConfig, getBackend, setBackend } from '@/api/apiClient';
import { cacheClear } from '@/lib/offlineCache';
import { clearOps } from '@/lib/outbox';

const DEFAULT_DB = 'test-sales';
const DEFAULT_API_BASE = 'dashboard/api';
// Backend of the last sign-in on this device — switching backends wipes the
// offline cache and queue, like switching Odoo servers does.
const LAST_LOGIN_BACKEND = 'sellgo_login_backend';

// Public trial accounts for the SellGo (Supabase) backend — supplied at build
// time via env (.env.local, never committed). They power the one-tap guest
// buttons, which render only when these values are present in the build.
const DEMO_MANAGER = {
  email: import.meta.env.VITE_DEMO_MANAGER_EMAIL,
  password: import.meta.env.VITE_DEMO_MANAGER_PW,
};
const DEMO_REP = {
  email: import.meta.env.VITE_DEMO_REP_EMAIL,
  password: import.meta.env.VITE_DEMO_REP_PW,
};
const HAS_DEMO = Boolean(DEMO_MANAGER.email && DEMO_REP.email);

/**
 * Customers on SellGo's own database get a link ending in ?connect=sellgo
 * (?connect=odoo switches back). It sets this device's backend once and is
 * then removed from the address bar, so the login screen itself never shows
 * a backend choice.
 */
function readConnectParam() {
  const params = new URLSearchParams(window.location.search);
  const target = { sellgo: 'supabase', odoo: 'odoo' }[params.get('connect')];
  if (!target) return null;
  setBackend(target);
  params.delete('connect');
  const rest = params.toString();
  window.history.replaceState({}, document.title, window.location.pathname + (rest ? `?${rest}` : ''));
  return target;
}

export default function OdooLogin() {
  const { loginOdoo } = useOdooAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetBanner, setShowResetBanner] = useState(false);
  // 'odoo' = the customer's own Odoo server, 'supabase' = SellGo's own
  // database (customers without Odoo). Comes from the ?connect= link or the
  // admin drawer — never shown on the login screen itself.
  const [backend, setBackendMode] = useState(() => readConnectParam() || getBackend());

  // Connection settings panel — hidden by default. Surface it only via
  // a "secret handshake" (5 taps on the logo) so admins can recover
  // when defaults need to change, without confusing end users.
  const [showConfig, setShowConfig] = useState(
    () => getBackend() === 'odoo' && !localStorage.getItem('dashboard_server_url')
  );
  const [logoTapCount, setLogoTapCount] = useState(0);
  // SaaS mode: each tenant may point the app at its own Odoo server. We now
  // keep an absolute serverUrl (the addon serves CORS), so initialise the
  // field from storage instead of forcing it empty.
  const [serverUrl, setServerUrl] = useState(localStorage.getItem('dashboard_server_url') || '');
  const [database, setDatabase] = useState(getOdooDatabase() || DEFAULT_DB);
  const [apiBase, setApiBase] = useState(
    localStorage.getItem('dashboard_api_base_path') || DEFAULT_API_BASE
  );

  // Force-persist the safe defaults on every page load so a wiped
  // browser (Clear Site Data, Incognito, new device) immediately works
  // without the user being asked to fill in the connection fields.
  //
  // The web app MUST NOT have an absolute serverUrl set — requests have
  // to stay relative (`/dashboard/api/...`) so the Vercel rewrite hands
  // them to the Odoo backend. An absolute URL pointing at `el-fath.co`
  // would force a cross-origin call, which Odoo rejects (CORS) →
  // "Failed to fetch". We therefore unconditionally strip any
  // ``dashboard_server_url`` we find on mount.
  //
  // The native (Capacitor) build still needs an absolute URL because
  // its origin is `capacitor://localhost` — but `getApiBaseUrl()` in
  // apiClient.js already handles that case via PROD_SERVER_URL, so we
  // don't need to write to localStorage from here either.
  // Backfill DB + api base if they're missing so a freshly-wiped browser
  // still has sane defaults. We no longer strip an absolute serverUrl —
  // SaaS tenants point the app at their own Odoo (the addon serves CORS).
  useEffect(() => {
    const hasDb = !!localStorage.getItem('dashboard_odoo_database');
    const hasApi = !!localStorage.getItem('dashboard_api_base_path');
    if (!hasDb || !hasApi) {
      setApiConfig({
        database: hasDb ? undefined : DEFAULT_DB,
        apiBasePath: hasApi ? undefined : DEFAULT_API_BASE,
      });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('config_changed') === 'true') {
      setShowResetBanner(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Secret handshake: 5 taps on the logo reveals the connection drawer.
  // The counter resets after 1.5s of no taps so accidental triggering
  // doesn't happen.
  const handleLogoTap = () => {
    setLogoTapCount(c => {
      const next = c + 1;
      if (next >= 5) {
        setShowConfig(true);
        return 0;
      }
      return next;
    });
  };
  useEffect(() => {
    if (logoTapCount === 0) return;
    const t = setTimeout(() => setLogoTapCount(0), 1500);
    return () => clearTimeout(t);
  }, [logoTapCount]);

  const persistConfig = () => {
    setBackend(backend);
    if (backend === 'odoo') {
      setApiConfig({
        serverUrl: serverUrl.trim() || null,
        apiBasePath: apiBase.trim() || DEFAULT_API_BASE,
        database: database.trim() || DEFAULT_DB,
      });
    }
    setShowConfig(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }
    // SaaS: the server link is the tenant identity — it's mandatory. Without it
    // we must NOT silently fall back to a default server.
    if (backend === 'odoo' && !serverUrl.trim()) {
      setShowConfig(true);
      setError('من فضلك أدخل رابط الخادم الخاص بك أولاً');
      return;
    }
    if (backend === 'odoo' && !database.trim()) {
      setShowConfig(true);
      setError('من فضلك أدخل اسم قاعدة البيانات');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const switchingBackend = (localStorage.getItem(LAST_LOGIN_BACKEND) || 'odoo') !== backend;

      // SellGo's own database: the account itself says which company it belongs to.
      if (backend === 'supabase') {
        if (switchingBackend) {
          try { await cacheClear(); } catch { /* ignore */ }
          try { await clearOps(); } catch { /* ignore */ }
        }
        setBackend('supabase');
        localStorage.setItem(LAST_LOGIN_BACKEND, 'supabase');
        await loginOdoo({ db: '', login: email.trim(), password });
        return;
      }

      // Make sure the latest config is persisted before attempting login.
      // (User might have edited fields without clicking "حفظ" first.)
      const db = database.trim();
      const newServer = serverUrl.trim();

      // If we're switching to a DIFFERENT tenant (server or database), wipe
      // the offline read-cache and any queued operations so one tenant's data
      // can never leak into another's.
      const prevDb = localStorage.getItem('dashboard_odoo_database') || '';
      const prevServer = localStorage.getItem('dashboard_server_url') || '';
      if (prevDb !== db || prevServer !== newServer || switchingBackend) {
        try { await cacheClear(); } catch { /* ignore */ }
        try { await clearOps(); } catch { /* ignore */ }
      }

      setBackend('odoo');
      localStorage.removeItem(LAST_LOGIN_BACKEND);
      setApiConfig({
        serverUrl: newServer || null,
        apiBasePath: apiBase.trim() || DEFAULT_API_BASE,
        database: db,
      });
      await loginOdoo({ db, login: email.trim(), password });
      // The provider sets the session; App.jsx will swap to <AppLayout />.
    } catch (e2) {
      setError(
        e2?.message ||
          'بيانات الدخول غير صحيحة أو المستخدم غير مصرح له بالدخول إلى قاعدة البيانات.'
      );
    } finally {
      setLoading(false);
    }
  };

  // One-tap guest login for the public demo (SellGo / Supabase backend only).
  const handleGuest = async (acct) => {
    setLoading(true);
    setError('');
    try {
      const switchingBackend = (localStorage.getItem(LAST_LOGIN_BACKEND) || 'odoo') !== 'supabase';
      if (switchingBackend) {
        try { await cacheClear(); } catch { /* ignore */ }
        try { await clearOps(); } catch { /* ignore */ }
      }
      setBackend('supabase');
      localStorage.setItem(LAST_LOGIN_BACKEND, 'supabase');
      await loginOdoo({ db: '', login: acct.email, password: acct.password });
    } catch (e2) {
      setError(e2?.message || 'تعذّر الدخول التجريبي، حاول مرة أخرى.');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      dir="rtl"
      style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)' }}
    >
      <div className="w-full max-w-sm space-y-5">
        {/* Reset Banner */}
        {showResetBanner && (
          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl px-4 py-3 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-emerald-300 font-bold mb-1">✓ تم تحديث إعدادات الخادم</p>
              <p className="text-emerald-200/70 text-xs">
                تم إعادة تهيئة التطبيق. سيتم مزامنة البيانات بعد تسجيل الدخول.
              </p>
            </div>
          </div>
        )}

        {/* Logo (5 taps on the logo reveals the hidden config drawer) */}
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={handleLogoTap}
            className="focus:outline-none active:scale-95 transition-transform"
            aria-label="logo"
          >
            <SellGoLogo size={96} />
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-black tracking-tight">
              <span className="text-white">Sell</span>
              <span style={{ color: '#10B981' }}>Go</span>
            </h1>
            <p className="text-sm text-white/70 font-medium mt-1">نظام مبيعات وتوزيع متكامل</p>
            {logoTapCount >= 3 && (
              <p className="text-[10px] text-white/40 mt-1">
                {5 - logoTapCount} ضغطة لفتح إعدادات الأدمن
              </p>
            )}
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl space-y-4">
          {/* Config drawer — only visible via the 5-tap secret on the logo */}
          {showConfig && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-3 relative">
              <button
                type="button"
                onClick={() => setShowConfig(false)}
                className="absolute top-2 left-2 p-1 rounded-md hover:bg-blue-100 text-muted-foreground"
                aria-label="close"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="text-xs font-bold text-blue-700">إعدادات الاتصال (الأدمن فقط)</p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">مصدر البيانات</label>
                <div className="grid grid-cols-2 gap-1 p-1 bg-white border border-blue-200 rounded-lg">
                  {[
                    ['odoo', 'Odoo'],
                    ['supabase', 'SellGo'],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setBackendMode(key);
                        setError('');
                      }}
                      className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        backend === key ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {backend === 'odoo' && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">رابط الخادم</label>
                    <Input
                      type="url"
                      value={serverUrl}
                      onChange={(e) => setServerUrl(e.target.value)}
                      placeholder="https://your-odoo.example.com"
                      className="rounded-lg text-sm"
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">قاعدة البيانات</label>
                    <Input
                      value={database}
                      onChange={(e) => setDatabase(e.target.value)}
                      placeholder="database-name"
                      className="rounded-lg text-sm"
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">مسار API الأساسي</label>
                    <Input
                      value={apiBase}
                      onChange={(e) => setApiBase(e.target.value)}
                      placeholder="dashboard/api"
                      className="rounded-lg text-sm"
                      dir="ltr"
                    />
                  </div>
                </>
              )}
              <Button
                type="button"
                onClick={persistConfig}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                حفظ
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">البريد الإلكتروني</label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@company.com"
                  className="pr-10 rounded-xl"
                  autoComplete="email"
                  autoFocus
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">كلمة المرور</label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10 pl-10 rounded-xl"
                  autoComplete="current-password"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full bg-primary" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري التحقق...
                </span>
              ) : (
                'تسجيل الدخول'
              )}
            </Button>
          </form>

          {backend === 'supabase' && HAS_DEMO && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-muted-foreground">أو</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  onClick={() => handleGuest(DEMO_MANAGER)}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-blue-500 text-blue-700 hover:bg-blue-50 font-bold"
                >
                  👔 دخول كمدير
                </Button>
                <Button
                  type="button"
                  onClick={() => handleGuest(DEMO_REP)}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-bold"
                >
                  🚗 دخول كمندوب
                </Button>
              </div>
              <p className="text-center text-[11px] text-muted-foreground">
                تجربة كاملة ببيانات وهمية — بدون تسجيل
              </p>
            </>
          )}
        </div>

        <div className="flex flex-col items-center gap-0.5 pt-2">
          <p className="text-white/50 text-xs">
            {backend === 'supabase' ? 'يتم التحقق من هويتك بشكل آمن' : 'يتم التحقق من هويتك مباشرة عبر Odoo'}
          </p>
          <div className="mt-3 w-16 h-px bg-white/20" />
          <div className="mt-3 flex flex-col items-center">
            <span className="text-white/40" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
              Powered by
            </span>
            <span className="font-black text-sm tracking-wider" style={{ color: '#D85A4A' }}>
              TECH SHARK
            </span>
            <span className="text-white/30" style={{ fontSize: '9px', letterSpacing: '2px' }}>
              FOR TECHNOLOGY
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
